import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { useFirebase } from '../FirebaseContext';
import logo from '../assets/spario.png';
import {
  SquaresFour,
  ChatCircleDots,
  Bell,
  ChartLineUp,
  Receipt,
  Wallet,
  Handshake,
  CurrencyCircleDollar,
  SteeringWheel,
  UsersThree,
  Car,
  FileText,
  FolderOpen,
  Export,
  ChartBar,
  ShieldCheck,
  CaretLeft,
} from '@phosphor-icons/react';
import './SideMenu.css';

const ICON_SIZE = 20;
const ICON_WEIGHT = 'duotone';

const SideMenu = ({ collapsed, onCollapse }) => {
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const { currentFirebase } = useFirebase();
  const db = currentFirebase.database;
  const auth = currentFirebase.auth;
  const userEmail = auth?.currentUser?.email?.toLowerCase() || '';

  const [hasKsefAccess, setHasKsefAccess] = useState(false);

  useEffect(() => {
    const accessRef = ref(db, 'ksefAccessEmails');
    const unsubscribe = onValue(accessRef, (snapshot) => {
      if (snapshot.exists()) {
        const emails = Object.values(snapshot.val()).map(e => e.toLowerCase());
        setHasKsefAccess(emails.includes(userEmail));
      } else {
        setHasKsefAccess(false);
      }
    });
    return () => unsubscribe();
  }, [db, userEmail]);

  useEffect(() => {
    const messagesRef = ref(db, 'chats');
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      let unreadCount = 0;
      snapshot.forEach((chatSnap) => {
        const chat = chatSnap.val();
        if (chat.messages) {
          Object.values(chat.messages).forEach((msg) => {
            if (!msg.isRead && msg.senderRole !== 'admin') {
              unreadCount++;
            }
          });
        }
      });
      setUnreadMessagesCount(unreadCount);
    });
    return () => unsubscribe();
  }, [db]);

  const menuGroups = [
    {
      label: 'Przegląd',
      items: [
        { key: '/', label: 'Dashboard', icon: SquaresFour },
      ],
    },
    {
      label: 'Komunikacja',
      items: [
        { key: '/chat', label: 'Chat', icon: ChatCircleDots, badge: unreadMessagesCount },
        { key: '/powiadomienia', label: 'Powiadomienia', icon: Bell },
      ],
    },
    {
      label: 'Rozliczenia',
      items: [
        { key: '/rozliczenie', label: 'Rozliczenie', icon: ChartLineUp },
        { key: '/faktury', label: 'Faktury', icon: Receipt },
        { key: '/wydatki', label: 'Wydatki', icon: Wallet },
        { key: '/bonus', label: 'Bonus Partnera', icon: Handshake },
        { key: '/przelewy', label: 'Przelewy', icon: CurrencyCircleDollar },
      ],
    },
    {
      label: 'Zarządzaj',
      items: [
        { key: '/kierowcy', label: 'Kierowcy', icon: SteeringWheel },
        { key: '/uzytkownicy', label: 'Użytkownicy', icon: UsersThree },
      ],
    },
    {
      label: 'Flota',
      items: [
        { key: '/samochody', label: 'Samochody', icon: Car },
      ],
    },
    {
      label: 'Dokumenty',
      items: [
        { key: '/template', label: 'Szablony', icon: FileText },
        { key: '/moje-dokumenty', label: 'Moje dokumenty', icon: FolderOpen },
        { key: '/export-xml-comarch', label: 'Eksport XML', icon: Export },
        { key: '/raporty', label: 'Raporty', icon: ChartBar },
      ],
    },
    ...(hasKsefAccess ? [{
      label: 'Ustawienia',
      items: [
        { key: '/ksef', label: 'KSeF', icon: ShieldCheck },
      ],
    }] : []),
  ];

  const handleClick = (key) => {
    navigate(key);
  };

  return (
    <nav className="side-menu">
      {/* Logo + collapse toggle */}
      <div className={`sm-header${collapsed ? ' collapsed' : ''}`}>
        <div className="sm-logo">
          <img
            src={logo}
            alt="Spario"
            className={`sm-logo-img${collapsed ? ' collapsed' : ''}`}
          />
        </div>
        {onCollapse && (
          <button
            className={`sm-collapse-btn${collapsed ? ' collapsed' : ''}`}
            onClick={onCollapse}
            aria-label={collapsed ? 'Rozwiń menu' : 'Zwiń menu'}
          >
            <CaretLeft size={16} weight="bold" />
          </button>
        )}
      </div>

      {/* Menu groups */}
      {menuGroups.map((group, gi) => (
        <div className="sm-group" key={gi}>
          <div className={`sm-group-label${collapsed ? ' collapsed' : ''}`}>
            {group.label}
          </div>
          {group.items.map((item) => {
            const isActive = location.pathname === item.key;
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                className={`sm-item${isActive ? ' active' : ''}${collapsed ? ' collapsed' : ''}`}
                onClick={() => handleClick(item.key)}
                {...(collapsed ? { 'data-tooltip': item.label } : {})}
              >
                <span className="sm-icon">
                  <Icon size={ICON_SIZE} weight={isActive ? 'fill' : ICON_WEIGHT} />
                </span>
                <span className={`sm-label${collapsed ? ' hidden' : ''}`}>
                  {item.label}
                </span>
                {item.badge > 0 && (
                  <span className={`sm-badge${collapsed ? ' collapsed' : ''}`}>
                    {!collapsed && item.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </nav>
  );
};

export default SideMenu;
